import { useCallback, useEffect, useRef, useState } from "react";
import AppHeader from "@/components/AppHeader";
import AddFreelancerDialog from "@/components/AddFreelancerDialog";
import ResetPasswordDialog from "@/components/ResetPasswordDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import { formatFriendlyDate, formatRelativeTime } from "@/lib/dateUtils";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  ShieldCheck,
  UserCheck,
  UserX,
  Search,
  Users,
  KeyRound,
  ChevronLeft,
  ChevronRight,
  DollarSign,
} from "lucide-react";

export default function AdminTeamPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [usersMeta, setUsersMeta] = useState(null);

  const [statusTarget, setStatusTarget] = useState(null); // user to toggle
  const [toggling, setToggling] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // user to reset password for
  const [flash, setFlash] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/users", {
        params: {
          role: "freelancer",
          search: search.trim() || undefined,
          page,
          page_size: 50,
        },
      });
      setUsers(data.users || data);
      setUsersMeta(data.users ? data : null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load users.");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  // The /admin/users endpoint already filters by role=freelancer server-side,
  // so the rows we get back are the rows we render — no extra client-side work.
  const freelancers = users;

  const freelancerCount = usersMeta?.freelancer_count ?? freelancers.length;
  const activeCount =
    usersMeta?.active_freelancers ?? freelancers.filter((u) => u.is_active).length;
  const inactiveCount =
    usersMeta?.inactive_freelancers ?? Math.max(freelancerCount - activeCount, 0);
  const adminCount = usersMeta?.admin_count ?? 0;

  const confirmToggleStatus = async () => {
    if (!statusTarget) return;
    setToggling(true);
    try {
      await api.patch(`/admin/users/${statusTarget.id}/status`, {
        is_active: !statusTarget.is_active,
      });
      const action = !statusTarget.is_active ? "reactivated" : "deactivated";
      toast.success(`${statusTarget.name} ${action}`);
      setFlash(`${statusTarget.name} ${action}.`);
      setStatusTarget(null);
      await load();
      setTimeout(() => setFlash(""), 3000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not update status.";
      toast.error(msg);
      setError(msg);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main
        className="max-w-7xl mx-auto px-4 sm:px-6 py-8"
        data-testid="admin-team-page"
      >
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Team</h2>
            <p className="text-sm text-slate-500 mt-1">
              Create and manage freelancer accounts.
            </p>
          </div>
          <button
            onClick={() => setAddOpen(true)}
            data-testid="add-freelancer-button"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition shadow-sm"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            <Plus size={16} />
            Add freelancer
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            label="Freelancers"
            value={freelancerCount}
            sub={`${activeCount} active · ${inactiveCount} inactive`}
            Icon={Users}
            color={BRAND.teal}
          />
          <SummaryCard
            label="Active"
            value={activeCount}
            sub="Can log in and track time"
            Icon={UserCheck}
            color={BRAND.teal}
            testId="team-active-count"
          />
          <SummaryCard
            label="Admins"
            value={adminCount}
            sub="Have full access"
            Icon={ShieldCheck}
            color={BRAND.purple}
          />
        </div>

        {flash && (
          <div
            className="mb-4 px-3 py-2 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm"
            data-testid="team-flash"
          >
            {flash}
          </div>
        )}
        {error && (
          <div
            className="mb-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm"
            data-testid="team-error"
          >
            {error}
          </div>
        )}

        {/* Search */}
        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or email…"
              data-testid="team-search"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
            />
          </div>
        </div>

        {/* Freelancer table */}
        <div
          className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
          data-testid="team-table"
        >
          {loading ? (
            <div className="p-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-slate-400" size={20} />
            </div>
          ) : freelancers.length === 0 ? (
            <div className="p-10 text-center">
              {freelancers.length === 0 ? (
                <>
                  <div
                    className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
                    style={{ background: `${BRAND.teal}12` }}
                  >
                    <Users style={{ color: BRAND.teal }} />
                  </div>
                  <h4 className="mt-3 text-sm font-semibold text-slate-700">
                    No freelancers yet
                  </h4>
                  <p className="mt-1 text-xs text-slate-500">
                    Click “Add freelancer” to create the first account.
                  </p>
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  No freelancers match “{search}”.
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                      <th className="text-left font-semibold px-4 py-3">Name</th>
                      <th className="text-left font-semibold px-4 py-3">Email</th>
                      <th className="text-left font-semibold px-4 py-3">Status</th>
                      <th className="text-right font-semibold px-4 py-3">Rate</th>
                      <th className="text-center font-semibold px-4 py-3">Commissions</th>
                      <th className="text-left font-semibold px-4 py-3">Last seen</th>
                      <th className="text-left font-semibold px-4 py-3">Joined</th>
                      <th className="text-right font-semibold px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freelancers.map((u) => (
                      <tr
                        key={u.id}
                        className="border-t border-slate-100 hover:bg-slate-50/50 transition"
                        data-testid={`team-row-${u.id}`}
                      >
                        <td className="px-4 py-3 font-medium text-slate-800">
                          {u.name}
                        </td>
                        <td className="px-4 py-3 text-slate-600">{u.email}</td>
                        <td className="px-4 py-3">
                          {u.is_active ? (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full"
                              style={{
                                background: `${BRAND.teal}15`,
                                color: BRAND.teal,
                              }}
                            >
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND.teal }} />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <RateCell user={u} onSaved={load} />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <CommissionsToggle user={u} onSaved={load} />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {u.last_login_at ? (
                            <span
                              className="text-slate-700"
                              title={new Date(u.last_login_at).toLocaleString()}
                              data-testid={`last-seen-${u.id}`}
                            >
                              {formatRelativeTime(u.last_login_at)}
                            </span>
                          ) : (
                            <span className="text-slate-400">Never</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {u.created_at ? formatFriendlyDate(String(u.created_at).slice(0, 10)) : "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => setResetTarget(u)}
                              data-testid={`reset-password-${u.id}`}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border text-slate-600 border-slate-200 hover:bg-slate-50 transition"
                              title="Reset password"
                            >
                              <KeyRound size={13} />
                              <span className="hidden sm:inline">Reset password</span>
                            </button>
                            <button
                              onClick={() => setStatusTarget(u)}
                              data-testid={`toggle-status-${u.id}`}
                              className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border transition ${
                                u.is_active
                                  ? "text-red-600 border-red-200 hover:bg-red-50"
                                  : "text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                              }`}
                            >
                              {u.is_active ? <UserX size={13} /> : <UserCheck size={13} />}
                              {u.is_active ? "Deactivate" : "Reactivate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <TeamPagination meta={usersMeta} onPageChange={setPage} />
            </>
          )}
        </div>
      </main>

      <AddFreelancerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={(u) => {
          toast.success(`${u.name} added`);
          setFlash(`${u.name} added.`);
          setTimeout(() => setFlash(""), 3000);
          load();
        }}
      />

      <ConfirmDialog
        open={!!statusTarget}
        onClose={() => !toggling && setStatusTarget(null)}
        onConfirm={confirmToggleStatus}
        confirming={toggling}
        title={
          statusTarget?.is_active
            ? "Deactivate freelancer?"
            : "Reactivate freelancer?"
        }
        description={
          statusTarget
            ? statusTarget.is_active
              ? `${statusTarget.name} will no longer be able to sign in. Their past entries remain visible. You can reactivate anytime.`
              : `${statusTarget.name} will be able to sign in and log time again.`
            : ""
        }
        confirmLabel={statusTarget?.is_active ? "Deactivate" : "Reactivate"}
        danger={!!statusTarget?.is_active}
      />

      <ResetPasswordDialog
        user={resetTarget}
        onClose={() => setResetTarget(null)}
      />
    </div>
  );
}

function SummaryCard({ label, value, sub, Icon, color, testId }) {
  return (
    <div className="rounded-2xl p-5 bg-white border border-slate-200">
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
        <Icon size={14} style={{ color }} />
        {label}
      </div>
      <div
        className="mt-1 text-3xl font-bold text-slate-900 tabular-nums"
        data-testid={testId}
      >
        {value}
      </div>
      <div className="mt-1 text-sm text-slate-500">{sub}</div>
    </div>
  );
}

function TeamPagination({ meta, onPageChange }) {
  if (!meta || meta.total_pages <= 1) return null;
  const first = (meta.page - 1) * meta.page_size + 1;
  const last = Math.min(meta.page * meta.page_size, meta.total_count);
  return (
    <div className="border-t border-slate-100 px-4 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
      <div className="text-slate-500">
        Showing {first}-{last} of {meta.total_count}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(meta.page - 1, 1))}
          disabled={meta.page <= 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
        >
          <ChevronLeft size={14} />
          Previous
        </button>
        <span className="text-slate-500 tabular-nums">
          Page {meta.page} of {meta.total_pages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(meta.page + 1, meta.total_pages))}
          disabled={meta.page >= meta.total_pages}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
        >
          Next
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

// Inline-editable hourly rate. Click the cell to edit; blur or Enter saves;
// Escape cancels. Empty input clears the rate (cost calc skips that user).
function RateCell({ user, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(
    user.hourly_rate != null ? String(user.hourly_rate) : ""
  );
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setValue(user.hourly_rate != null ? String(user.hourly_rate) : "");
  }, [user.hourly_rate]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const cancel = () => {
    setValue(user.hourly_rate != null ? String(user.hourly_rate) : "");
    setEditing(false);
  };

  const save = async () => {
    const trimmed = value.trim();
    let next = null;
    if (trimmed !== "") {
      const parsed = parseFloat(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        toast.error("Rate must be a positive number.");
        inputRef.current?.focus();
        return;
      }
      next = Math.round(parsed * 100) / 100;
    }
    if (next === (user.hourly_rate ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}/rate`, { hourly_rate: next });
      toast.success(
        next === null
          ? `Rate cleared for ${user.name}`
          : `Rate set to $${next.toFixed(2)}/hr for ${user.name}`
      );
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update rate.");
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-1">
        <span className="text-slate-500 text-xs">$</span>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              e.preventDefault();
              cancel();
            }
          }}
          disabled={saving}
          data-testid={`rate-input-${user.id}`}
          className="w-20 px-2 py-1 text-right text-sm rounded border border-slate-300 focus:outline-none focus:ring-2"
          style={{ "--tw-ring-color": BRAND.teal }}
          placeholder="—"
        />
        <span className="text-slate-400 text-xs">/hr</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      data-testid={`rate-cell-${user.id}`}
      title="Click to set hourly rate"
      className="text-sm tabular-nums text-slate-700 hover:text-slate-900 hover:bg-slate-100 px-2 py-0.5 rounded transition"
    >
      {user.hourly_rate != null ? (
        <>
          ${Number(user.hourly_rate).toFixed(2)}
          <span className="text-slate-400 text-xs">/hr</span>
        </>
      ) : (
        <span className="text-slate-400">Set rate</span>
      )}
    </button>
  );
}

// Per-freelancer toggle for the Commissions module. Off by default — most
// freelancers don't book clients on commission, so showing the UI to them
// would be noise. Click flips it; saves immediately.
function CommissionsToggle({ user, onSaved }) {
  const [saving, setSaving] = useState(false);
  const enabled = !!user.commissions_enabled;

  const toggle = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/users/${user.id}/commissions`, { enabled: !enabled });
      toast.success(
        !enabled
          ? `Commissions enabled for ${user.name}`
          : `Commissions disabled for ${user.name}`
      );
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update commissions setting.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving}
      data-testid={`commissions-toggle-${user.id}`}
      title={enabled ? "Click to disable commissions" : "Click to enable commissions"}
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full transition ${
        saving ? "opacity-60 cursor-wait" : "cursor-pointer hover:opacity-80"
      }`}
      style={
        enabled
          ? { background: `${BRAND.teal}15`, color: BRAND.teal }
          : { background: "#94a3b820", color: "#475569" }
      }
    >
      <DollarSign size={10} />
      {enabled ? "On" : "Off"}
    </button>
  );
}
